// Safety Dashboard — admin-only crash-correlation analytics for RSA-EXISTING
// corridors. Productizes the SR-2 study: pick a state db + one or more surveyed
// corridors, see deficiency runs vs MassDOT crash data on an OSM map, a
// milepost strip chart, descriptive stats that re-slice instantly on filter
// change, per-crash and per-cluster insights, the cached statistical verdict
// (GET /api/safety/verdict) and the CMF countermeasure program.
//
// Data flow: state -> collections -> per corridor (corridor P/N, analyses,
// crashes, verdict) fetched ONCE; every FilterBar control re-filters
// client-side via safetyFilters.js — filters never refetch. Runs, crashes and
// sites carry a `corridor` tag so mileposts of different routes never mix.
import React, { useEffect, useMemo, useState } from 'react';
import * as api from './safetyApi';
import {
  DEFAULT_FILTERS, RUN_ANALYSES, analysisOf, selectedAnalyses, applyCorrelatedOnly, clusterDeficiencies, clusterVisibleCrashes,
  computeDescriptiveStats, computeSiteTable, deriveIntersections, filterCrashes, filterRuns,
  intersectionStats,
} from './safetyFilters';
import { computeImpactSummary, visibleFindings } from './insightsText';
import FilterBar from './FilterBar';
import CorridorPicker, { MultiPicker, corridorKeyOf } from './CorridorPicker';
import SettingsDialog, { loadDisabledRules, saveDisabledRules } from './SettingsDialog';
import SafetyMap from './SafetyMap';
import StripChart from './StripChart';
import SummaryStats from './SummaryStats';
import SitesTable from './SitesTable';
import CrashClustersTable from './CrashClustersTable';
import VerdictCard, { VERDICT_OPTIONS } from './VerdictCard';
import InsightsPanel from './InsightsPanel';
import ClusterInsightsPanel from './ClusterInsightsPanel';
import IntersectionsPanel from './IntersectionsPanel';
import ProgramPanel from './ProgramPanel';
import { CMF_POLICIES, buildProgram, exposureYears, siteKeyOf, siteRecommendation } from './cmfBenefit';
import './SafetyDashboard.css';

const ANALYSES = RUN_ANALYSES;           // SSD PSD GLR FSA HCA GRW ISD (ALL is a view, not a fetch)
const DIRECTIONS = ['P', 'N'];

// Collapsible section (same pattern as UsageDashboard's SectionCard).
const SectionCard = ({ title, meta, children, defaultOpen = true, className = '' }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`safety-section ${className}`}>
      <button type="button" className="safety-section-head" onClick={() => setOpen(!open)}
        aria-expanded={open}>
        <span className={`safety-section-chevron ${open ? 'open' : ''}`}>▸</span>
        <span className="safety-section-title">{title}</span>
        {meta && <span className="safety-section-meta">{meta}</span>}
      </button>
      {open && <div className="safety-section-body">{children}</div>}
    </div>
  );
};

const splitKey = (key) => key.split('|');

// Collapsible sub-card (one per corridor inside a section).
const SubCard = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="safety-subcard">
      <button type="button" className="safety-subcard-head" onClick={() => setOpen(!open)} aria-expanded={open}>
        <span className={`safety-section-chevron ${open ? 'open' : ''}`}>▸</span>{title}
      </button>
      {open && <div className="safety-subcard-body">{children}</div>}
    </div>
  );
};

export default function SafetyDashboard() {
  const [states, setStates] = useState([]);
  const [stateId, setStateId] = useState('');
  const [corridors, setCorridorList] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);   // ["route|pathId", ...]
  const [filters, setFilters] = useState(() => ({ ...DEFAULT_FILTERS, disabledRules: loadDisabledRules() }));
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [verdictShow, setVerdictShow] = useState([]);   // [] = every verdict outcome

  // per-corridor payloads: {key: {corridor: {P,N}, analysis: {"P:SSD": payload}, crashes, verdict}}
  const [data, setData] = useState({});
  const [loading, setLoading] = useState({});
  const [errors, setErrors] = useState({});
  const [recomputing, setRecomputing] = useState(false);
  const [focus, setFocus] = useState(null); // row-click -> map fly-to target
  const [cmfLibrary, setCmfLibrary] = useState(null);   // curated FHWA CMF entries
  const [unitCostOverrides, setUnitCostOverrides] = useState({}); // "siteKey:crfid" -> USD

  const analysis = analysisOf(filters);            // single key or 'ALL'
  const mark = (key, on) => setLoading((l) => ({ ...l, [key]: on }));
  const fail = (key, e) => setErrors((er) => ({ ...er, [key]: e.message }));
  const patchData = (key, patch) => setData((d) => ({ ...d, [key]: { ...(d[key] || {}), ...patch } }));

  useEffect(() => {
    api.fetchStates()
      .then((r) => {
        setStates(r.states || []);
        if (r.states?.length === 1) setStateId(r.states[0].id);
      })
      .catch((e) => fail('states', e));
    api.fetchCmfLibrary()
      .then((r) => setCmfLibrary(r.library || null))
      .catch((e) => fail('cmf', e));
  }, []);

  useEffect(() => {
    if (!stateId) return;
    setCorridorList([]);
    setSelectedKeys([]);
    setData({});
    mark('collections', true);
    api.fetchCollections(stateId)
      .then((r) => setCorridorList(r.corridors || []))
      .catch((e) => fail('collections', e))
      .finally(() => mark('collections', false));
  }, [stateId]);

  // fetch newly selected corridors (never refetch the ones already loaded)
  useEffect(() => {
    if (!stateId) return;
    setData((d) => Object.fromEntries(Object.entries(d).filter(([k]) => selectedKeys.includes(k))));
    setErrors((er) => ({ states: er.states, collections: er.collections, cmf: er.cmf }));
    setFilters((f) => ({ ...f, mpRange: null }));
    selectedKeys.forEach((key) => {
      if (data[key]) return;
      const [route, pathId] = splitKey(key);
      patchData(key, { corridor: {}, analysis: {}, crashes: null, verdict: null });
      DIRECTIONS.forEach((dir) => {
        api.fetchCorridor(stateId, route, pathId, dir)
          .then((r) => setData((d) => d[key] ? { ...d, [key]: { ...d[key], corridor: { ...d[key].corridor, [dir]: r.corridor } } } : d))
          .catch(() => setData((d) => d[key] ? { ...d, [key]: { ...d[key], corridor: { ...d[key].corridor, [dir]: null } } } : d));
        ANALYSES.forEach((analysis) => {
          api.fetchAnalysis(stateId, route, pathId, dir, analysis)
            .then((r) => setData((d) => d[key] ? { ...d, [key]: { ...d[key], analysis: { ...d[key].analysis, [`${dir}:${analysis}`]: r } } } : d))
            .catch(() => setData((d) => d[key] ? { ...d, [key]: { ...d[key], analysis: { ...d[key].analysis, [`${dir}:${analysis}`]: null } } } : d));
        });
      });
      mark(`crashes:${key}`, true);
      api.fetchCrashes(stateId, route, pathId)
        .then((r) => setData((d) => d[key] ? { ...d, [key]: { ...d[key], crashes: r } } : d))
        .catch((e) => fail(`crashes:${key}`, e))
        .finally(() => mark(`crashes:${key}`, false));
      mark(`verdict:${key}`, true);
      api.fetchVerdict(stateId, route, pathId)
        .then((r) => setData((d) => d[key] ? { ...d, [key]: { ...d[key], verdict: r.verdict } } : d))
        .catch((e) => fail(`verdict:${key}`, e))
        .finally(() => mark(`verdict:${key}`, false));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateId, selectedKeys]);

  const recomputeVerdict = (key) => {
    if (!stateId || !key) return;
    const [route, pathId] = splitKey(key);
    setRecomputing(true);
    api.fetchVerdict(stateId, route, pathId, true)
      .then((r) => setData((d) => d[key] ? { ...d, [key]: { ...d[key], verdict: r.verdict } } : d))
      .catch((e) => fail(`verdict:${key}`, e))
      .finally(() => setRecomputing(false));
  };

  const setDisabledRules = (rules) => {
    saveDisabledRules(rules);
    setFilters((f) => ({ ...f, disabledRules: rules }));
  };

  // ---- per-corridor descriptors ----
  const loaded = useMemo(() => selectedKeys.map((key) => {
    const d = data[key] || {};
    const meta = corridors.find((c) => corridorKeyOf(c) === key);
    const primary = d.corridor?.P || d.corridor?.N || null;
    return {
      key,
      road: primary?.road || meta?.road || key.split('|')[0],
      P: d.corridor?.P || null, N: d.corridor?.N || null,
      primary,
      extent: primary ? [Math.min(...[d.corridor?.P, d.corridor?.N].filter(Boolean).map((c) => c.mpMin)),
        Math.max(...[d.corridor?.P, d.corridor?.N].filter(Boolean).map((c) => c.mpMax))] : null,
      analysis: d.analysis || {},
      crashes: d.crashes || null,
      verdict: d.verdict || null,
    };
  }), [selectedKeys, data, corridors]);
  const single = loaded.length === 1;
  const firstCrashPayload = loaded.map((c) => c.crashes).find((p) => p && p.datasetVersion !== 'unavailable') || null;

  // ---- derived (all client-side; recompute on every filter change) ----
  // 'ALL' merges every analysis' runs (tagged so map/table can tell them apart)
  const wantedKey = selectedAnalyses(filters).join(',');   // stable dep for the merge below
  const allRuns = useMemo(() => {
    const wanted = wantedKey.split(',');
    const out = [];
    for (const c of loaded) {
      for (const dir of DIRECTIONS) {
        for (const a of wanted) {
          for (const r of c.analysis[`${dir}:${a}`]?.runs || []) {
            out.push({ ...r, analysis: a, direction: dir, corridor: c.key, road: c.road });
          }
        }
      }
    }
    return out;
  }, [loaded, wantedKey]);
  const allCrashes = useMemo(() => loaded.flatMap((c) =>
    (c.crashes?.crashes || []).map((x) => ({ ...x, corridor: c.key, road: c.road }))), [loaded]);
  const allIntersections = useMemo(() => loaded.flatMap((c) =>
    (c.crashes?.intersections || []).map((ix) => ({ ...ix, corridor: c.key, road: c.road }))), [loaded]);

  const crashDataAvailable = !!firstCrashPayload && !Object.keys(errors).some((k) => k.startsWith('crashes:'));
  const { filteredRuns, filteredCrashes } = useMemo(() => {
    let runs = filterRuns(allRuns, filters);
    // analyses-only mode: crash layers/tables empty, runs untouched
    let crashes = filters.showCrashes ? filterCrashes(allCrashes, filters) : [];
    if (filters.aboveChanceOnly) {
      // only analyses whose verdict is "Above chance" on their corridor
      const assoc = new Set();
      for (const c of loaded) {
        for (const [a, v] of Object.entries(c.verdict?.perAnalysis || {})) {
          if (v.verdict === 'associated') assoc.add(`${c.key}|${a}`);
        }
      }
      runs = runs.filter((r) => assoc.has(`${r.corridor}|${r.analysis}`));
      crashes = crashes
        .map((c) => ({ ...c, findings: Object.fromEntries(Object.entries(c.findings || {})
          .filter(([a]) => assoc.has(`${c.corridor}|${a}`))) }))
        .filter((c) => Object.keys(c.findings).length > 0);
    }
    if (!filters.correlatedOnly) return { filteredRuns: runs, filteredCrashes: crashes };
    const kept = applyCorrelatedOnly(runs, crashes, analysis);
    return { filteredRuns: kept.runs, filteredCrashes: kept.crashes };
  }, [allRuns, allCrashes, filters, analysis, loaded]);

  // clusters recomputed from the VISIBLE crashes so they obey every filter
  // (year, severity, direction, correlated-only, ...)
  const visibleClusters = useMemo(() => clusterVisibleCrashes(filteredCrashes), [filteredCrashes]);
  const corridorExtent = single ? loaded[0].extent : null;
  const lengthMi = loaded.reduce((s, c) => s + (c.primary?.lengthMi || 0), 0) || null;
  const stats = useMemo(
    () => computeDescriptiveStats(filteredCrashes, filteredRuns, { lengthMi }, analysis),
    [filteredCrashes, filteredRuns, lengthMi, analysis]
  );
  const sites = useMemo(() => computeSiteTable(filteredRuns, filteredCrashes), [filteredRuns, filteredCrashes]);
  const costs = useMemo(() => firstCrashPayload?.costTable?.values || {}, [firstCrashPayload]);
  const deficiencyClusters = useMemo(
    () => clusterDeficiencies(filteredRuns, filters.showCrashes ? filteredCrashes : [], { costs }),
    [filteredRuns, filteredCrashes, filters.showCrashes, costs]);
  // intersections = RDV ISD intersections + the ones the crash records reveal
  // (MassDOT junction type), with the priorities-table figures per intersection
  const derivedIntersections = useMemo(
    () => deriveIntersections(allCrashes, allIntersections), [allCrashes, allIntersections]);
  const intersectionsWithStats = useMemo(
    () => intersectionStats(derivedIntersections, filters.showCrashes ? filteredCrashes : [], costs),
    [derivedIntersections, filteredCrashes, filters.showCrashes, costs]);

  // ---- CMF countermeasure layer: per-site recommendation + program ----
  const exposure = useMemo(
    () => exposureYears(firstCrashPayload?.datasetYears, filters.years),
    [firstCrashPayload, filters.years]);
  const cmfActive = filters.showCmf && filters.showCrashes && crashDataAvailable && !!cmfLibrary;
  const recommendations = useMemo(() => {
    if (!cmfActive) return [];
    const opts = {
      policy: filters.cmfPolicy, exposure: exposure || 1,
      costs, ratePct: filters.discountRate, unitCostOverrides,
    };
    return sites.map((s) => siteRecommendation(s, filteredCrashes, cmfLibrary, opts));
  }, [cmfActive, sites, filteredCrashes, cmfLibrary, filters.cmfPolicy, filters.discountRate,
    exposure, costs, unitCostOverrides]);
  const sitesWithCmf = useMemo(() => {
    if (!cmfActive) return sites;
    const byKey = Object.fromEntries(recommendations.map((r) => [r.key, r]));
    return sites.map((s) => ({ ...s, cmf: byKey[siteKeyOf(s)] }));
  }, [sites, recommendations, cmfActive]);
  const program = useMemo(() => (cmfActive ? buildProgram(recommendations) : null),
    [cmfActive, recommendations]);
  const policyLabel = (CMF_POLICIES.find((p) => p.key === filters.cmfPolicy) || CMF_POLICIES[0]).label;
  // economics tile: social cost of the correlated crashes + program benefit
  const economics = useMemo(() => {
    if (!filters.showCrashes || !crashDataAvailable) return null;
    const correlated = filteredCrashes.filter((c) => visibleFindings(c, analysis).length > 0);
    const s = computeImpactSummary(correlated, firstCrashPayload?.costTable);
    return { correlated: s.n, correlatedCost: s.cost, lives: s.lives,
      benefitPerYr: program ? program.totals.benefitPerYr : null };
  }, [filteredCrashes, analysis, filters.showCrashes, crashDataAvailable, firstCrashPayload, program]);
  const setUnitCost = (siteKey, crfid, value) =>
    setUnitCostOverrides((o) => ({ ...o, [`${siteKey}:${crfid}`]: value }));
  // program row click: fly to the site's run and open its popup
  const focusProgramRow = (f) => {
    if (f.lat != null) { setFocus(f); return; }
    const run = filteredRuns.find((r) => r.analysis === f.analysis && r.direction === f.direction
      && (r.corridor || '') === (f.corridor || '') && r.mpStart === f.mpStart && r.mpEnd === f.mpEnd);
    if (run) {
      setFocus({ bounds: (run.coords || []).filter((p) => p[0] != null),
        lat: run.coords?.[0]?.[0], lng: run.coords?.[0]?.[1], runKey: siteKeyOf(run), key: f.key });
    }
  };

  const corridorLabel = loaded.length === 1 && loaded[0].primary
    ? `${loaded[0].road} · MP ${loaded[0].primary.mpMin}–${loaded[0].primary.mpMax}`
    : loaded.length > 1 ? `${loaded.length} corridors · ${loaded.map((c) => c.road).join(', ')}` : '';
  const stripBlocks = useMemo(() => loaded.map((c) => ({
    key: c.key, road: c.road, extent: c.extent,
    analysisByDirection: { P: c.analysis[`P:${analysis}`], N: c.analysis[`N:${analysis}`] },
    crashes: filteredCrashes.filter((x) => x.corridor === c.key),
    runs: filteredRuns.filter((r) => r.corridor === c.key),
    intersections: intersectionsWithStats.filter((ix) => ix.corridor === c.key),
  })), [loaded, analysis, filteredCrashes, filteredRuns, intersectionsWithStats]);
  const anyCrashLoading = selectedKeys.some((k) => loading[`crashes:${k}`]);
  const crashErrors = Object.entries(errors).filter(([k]) => k.startsWith('crashes:')).map(([, v]) => v);
  const disabledCount = filters.disabledRules?.length || 0;
  const crashNoun = filters.excludeAnimal ? 'non-animal crash' : 'crash';

  return (
    <div className="safety-dashboard">
      <header className="safety-header">
        <div>
          <h1>Safety Dashboard</h1>
          <p className="safety-muted">
            RSA-EXISTING deficiencies vs. crash data{firstCrashPayload ? ` · dataset ${firstCrashPayload.datasetVersion}` : ''}
          </p>
        </div>
        <div className="safety-pickers">
          <label className="safety-filter">
            <span>State</span>
            <select value={stateId} onChange={(e) => setStateId(e.target.value)}>
              <option value="">Select…</option>
              {states.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label} ({s.crashSource})
                </option>
              ))}
            </select>
          </label>
          <CorridorPicker corridors={corridors} selected={selectedKeys} onChange={setSelectedKeys}
            loading={loading.collections} disabled={!stateId} />
          <div className="safety-filter">
            <span>&nbsp;</span>
            <button type="button" className={`safety-toggle safety-settings-btn ${disabledCount ? 'on' : ''}`}
              onClick={() => setSettingsOpen(true)}
              title="Correlation rules per analysis — read the definitions, switch rules on or off">
              ⚙ Settings{disabledCount ? ` · ${disabledCount} rule${disabledCount === 1 ? '' : 's'} off` : ''}
            </button>
          </div>
        </div>
      </header>
      <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)}
        disabledRules={filters.disabledRules || []} onChange={setDisabledRules} />

      {errors.states && <div className="safety-error">Failed loading states: {errors.states}</div>}
      {errors.collections && <div className="safety-error">Failed loading corridors: {errors.collections}</div>}

      {selectedKeys.length ? (
        <>
          <FilterBar filters={filters} setFilters={setFilters}
            crashes={allCrashes} corridorExtent={corridorExtent}
            datasetYears={firstCrashPayload?.datasetYears} singleCorridor={single} />

          {/* ---- map (wide) + stats column ---- */}
          <div className="safety-main">
            <div className="safety-map-pane">
              <SafetyMap corridors={loaded} runs={filteredRuns}
                basemap={(states.find((s) => s.id === stateId) || {}).basemap || null}
                crashes={filteredCrashes} clusters={visibleClusters}
                intersections={intersectionsWithStats}
                analysis={analysis} focus={focus}
                showCrashes={filters.showCrashes && crashDataAvailable}
                recommendations={recommendations} />
              {anyCrashLoading && <div className="safety-loading-overlay">Loading crashes…</div>}
              {crashErrors.length > 0 && filters.showCrashes && (
                <div className="safety-note">Crash data unavailable ({crashErrors[0]}) —
                  showing the RDV analyses on their own.</div>
              )}
              {!crashDataAvailable && loaded.some((c) => c.crashes !== null) && !crashErrors.length && (
                <div className="safety-note">No crash data is available for this state —
                  showing the RDV analyses on their own.</div>
              )}
            </div>
            <div className="safety-stats-col">
              <SummaryStats stats={stats} analysis={analysis} vertical economics={economics} />
            </div>
          </div>

          <SectionCard title="Milepost strip" meta={`${analysis} vs crash locations · ${corridorLabel}`}>
            <StripChart blocks={stripBlocks} filters={{ ...filters, analysis }} onFocus={setFocus} />
          </SectionCard>

          {/* ---- insights: crash-by-crash and cluster-by-cluster side by side ---- */}
          <div className={`safety-insights-grid ${filters.showCrashes && crashDataAvailable ? '' : 'single'}`}>
            {filters.showCrashes && crashDataAvailable && (
              <SectionCard title="Insights" meta={`${analysis === 'ALL' ? 'all analyses' : analysis} · crash-by-crash`}>
                <InsightsPanel crashes={filteredCrashes} road={loaded[0]?.road}
                  analysis={analysis} onFocus={setFocus}
                  costTable={firstCrashPayload?.costTable}
                  program={program} policyLabel={policyLabel} stats={stats} crashNoun={crashNoun} />
              </SectionCard>
            )}
            <SectionCard title="Cluster insights" meta="stretches with several deficiencies">
              <ClusterInsightsPanel clusters={deficiencyClusters} onFocus={setFocus}
                recommendations={recommendations} analysis={analysis} crashNoun={crashNoun}
                hasCrashData={filters.showCrashes && crashDataAvailable} />
            </SectionCard>
          </div>

          {filters.showCrashes && crashDataAvailable && (
            <SectionCard title="Statistical support" meta={corridorLabel}>
              <div className="safety-insights-nav safety-verdict-tools">
                <MultiPicker label="Show verdicts" options={VERDICT_OPTIONS} selected={verdictShow}
                  onChange={setVerdictShow} noun="verdicts" allMeansEmpty
                  title="Show only analyses whose verdict is one of these outcomes" />
              </div>
              {loaded.map((c) => (loaded.length > 1 ? (
                <SubCard key={c.key} title={`${c.road} · MP ${c.extent ? `${c.extent[0]}–${c.extent[1]}` : c.key}`}>
                  <VerdictCard verdict={c.verdict} loading={loading[`verdict:${c.key}`]}
                    error={errors[`verdict:${c.key}`]} show={verdictShow}
                    onRecompute={() => recomputeVerdict(c.key)} recomputing={recomputing} />
                </SubCard>
              ) : (
                <VerdictCard key={c.key} verdict={c.verdict} loading={loading[`verdict:${c.key}`]}
                  error={errors[`verdict:${c.key}`]} show={verdictShow}
                  onRecompute={() => recomputeVerdict(c.key)} recomputing={recomputing} />
              )))}
            </SectionCard>
          )}

          <SectionCard title={`Deficiency sites (${analysis})`}>
            <SitesTable sites={sitesWithCmf} analysis={analysis} onFocus={setFocus}
              crashes={filters.showCrashes ? filteredCrashes : []}
              showCmf={cmfActive} exposure={exposure} ratePct={filters.discountRate}
              showCorridor={!single} />
          </SectionCard>
          {filters.showCrashes && crashDataAvailable && (
            <SectionCard title="Crash clusters">
              <CrashClustersTable clusters={visibleClusters} crashes={filteredCrashes}
                runs={filteredRuns} analysis={analysis} onFocus={setFocus} />
            </SectionCard>
          )}

          {cmfActive && (
            <SectionCard title="Countermeasure program"
              meta={`ranked by benefit · ${firstCrashPayload?.costTable?.source || 'crash costs'} · FHWA CMF Clearinghouse`}>
              <ProgramPanel recommendations={recommendations} exposure={exposure}
                ratePct={filters.discountRate} costSource={firstCrashPayload?.costTable?.source}
                policyLabel={policyLabel} corridorLabel={corridorLabel}
                onUnitCost={setUnitCost} onFocus={focusProgramRow} />
            </SectionCard>
          )}
          {errors.cmf && filters.showCmf && (
            <div className="safety-note">CMF library unavailable ({errors.cmf}) — countermeasure
              columns are hidden.</div>
          )}
        </>
      ) : (
        <div className="safety-placeholder">
          Pick a state and one or more corridors to load their analyses and crash data.
        </div>
      )}

      {stateId && (
        <SectionCard title="Intersection priorities"
          meta="district-wide, from the crash snapshot" defaultOpen={false}>
          <IntersectionsPanel stateId={stateId} onFocus={setFocus}
            library={cmfLibrary} policy={filters.cmfPolicy}
            datasetYears={firstCrashPayload?.datasetYears} showCmf={filters.showCmf} />
        </SectionCard>
      )}
    </div>
  );
}
